import React, { useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../utils/redux';
import ChatbotClient from '../../utils/ChatbotClient';
import EndAssistantCallModal from '../modals/EndAssistantCallModal';
import {
  setCallStatus,
  setConnecting,
  setUltravoxStatus,
  setCallModalOpen,
  setMinimized,
  setBotTranscript,
  setUserTranscript,
  endCall,
  resetCallState,
} from '../../utils/redux/slices/CallSlice';

const GlobalCallManager: React.FC = () => {
  const dispatch = useDispatch();
  const callState = useSelector((state: RootState) => state.call);
  const chatbotClientRef = useRef<ChatbotClient | null>(null);

  const {
    isConnected,
    isConnecting,
    isCallModalOpen,
    isMinimized,
    ultravoxStatus,
    agentName,
    agentId,
    agentKey,
  } = callState;

  const initializeCall = useCallback(async () => {
    try {
      if (!chatbotClientRef.current) {
        chatbotClientRef.current = new ChatbotClient({
          agentId,
          apiKey: agentKey,
          onConnecting: () => {
            dispatch(setConnecting(true));
            dispatch(setUltravoxStatus('connecting'));
          },
          onConnected: () => {
            dispatch(setConnecting(false));
            dispatch(setCallStatus(true));
          },
          onDisconnected: () => {
            dispatch(setCallStatus(false));
            dispatch(setCallModalOpen(false));
            dispatch(setUltravoxStatus('disconnected'));
            chatbotClientRef.current = null;
          },
          onStatusChange: (status: string) => {
            dispatch(setUltravoxStatus(status));
            console.log('🎯 Ultravox Status Changed:', status);
          },
          onBotTranscript: transcript => {
            dispatch(setBotTranscript(transcript));
          },
          onUserTranscript: transcript => {
            dispatch(setUserTranscript(transcript));
          },
        });
      }
      await chatbotClientRef.current.connect();
    } catch (error) {
      dispatch(setCallModalOpen(false));
      dispatch(setConnecting(false));
      dispatch(setUltravoxStatus('disconnected'));
      console.error('Connection error:', error);
    }
  }, [agentId, agentKey, dispatch]);

  // Initialize call when startCall is dispatched
  useEffect(() => {
    if (
      isCallModalOpen &&
      isConnecting &&
      !chatbotClientRef.current &&
      agentId &&
      agentKey
    ) {
      initializeCall();
    }
  }, [isCallModalOpen, isConnecting, agentId, agentKey, initializeCall]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (chatbotClientRef.current) {
        chatbotClientRef.current.disconnect();
        chatbotClientRef.current = null;
      }
    };
  }, []);

  const handleEndCall = async () => {
    try {
      if (chatbotClientRef.current) {
        await chatbotClientRef.current.disconnect();
      }
    } catch (error) {
      console.error('Disconnection error:', error);
    } finally {
      chatbotClientRef.current = null;
      dispatch(endCall());
      dispatch(resetCallState());
    }
  };

  const handleMinimize = () => {
    dispatch(setMinimized(true));
  };

  const handleRestore = () => {
    dispatch(setMinimized(false));
  };

  // Don't render anything if no active call
  if (!isCallModalOpen) {
    return null;
  }

  return (
    <EndAssistantCallModal
      open={isCallModalOpen}
      onClose={handleEndCall}
      onMinimize={handleMinimize}
      onRestore={handleRestore}
      isConnecting={isConnecting}
      isConnected={isConnected}
      isMinimized={isMinimized}
      ultravoxStatus={ultravoxStatus}
      agentName={agentName}
    />
  );
};

export default GlobalCallManager;
